import { useEffect, useRef, useState } from 'react'

interface UseIntersectionObserverProps {
  /** Процент видимости элемента для срабатывания (0-1) */
  threshold?: number
  /** Отступы от viewport для срабатывания */
  rootMargin?: string
}

// Константы для гистерезиса
const HYSTERESIS_OFFSET = 0.1
const MIN_THRESHOLD = 0.01

/**
 * Хук для отслеживания пересечения элемента с viewport
 * Использует гистерезис для предотвращения мигания
 */
export const useIntersectionObserver = ({
  threshold = 0.1,
  rootMargin = '0px'
}: UseIntersectionObserverProps = {}) => {
  const [isIntersecting, setIsIntersecting] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const lastStateRef = useRef<boolean>(false)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    // Создаем два observer с разными порогами для гистерезиса
    const observerShow = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !lastStateRef.current) {
          setIsIntersecting(true)
          lastStateRef.current = true
        }
      },
      { 
        threshold: threshold, 
        rootMargin: rootMargin 
      }
    )

    const observerHide = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting && lastStateRef.current) {
          setIsIntersecting(false)
          lastStateRef.current = false
        }
      },
      { 
        threshold: Math.max(MIN_THRESHOLD, threshold - HYSTERESIS_OFFSET), // Более низкий порог для скрытия
        rootMargin: rootMargin 
      }
    )

    observerShow.observe(element)
    observerHide.observe(element)

    return () => {
      observerShow.disconnect()
      observerHide.disconnect()
    }
  }, [threshold, rootMargin])

  return {
    ref,
    isIntersecting
  }
}
